import { Injectable } from '@angular/core';
import { Attr, AttrPath } from '@cumlaude/metadata';
import { AttOverrides } from '@cumlaude/shared-dashboard-registry';
import { findOverride, getFieldsByForeignKey } from './metadata';
import { FilterName } from './filter-name';

/**
 * Equivalentie van velden in fac_aw_aanwezigheid en hetzelfde veld in het afspraakdomein: links de
 * leerlingvariant, rechts de lesvariant. Voor het wisselen tussen Aanwezigheid en Datavolledigheid, en
 * hergebruikt in de datavolledigheid-tegel van de maandpiramides. Een eventuele derde waarde is een
 * verouderd pad dat nog in opgeslagen of gedeelde url's kan staan.
 */
export const AW_AFSPRAAK_EQUIVALENTEN: [FilterName, FilterName, ...FilterName[]][] = [
	['aw_fun_nm_vak', 'aw_fun_nm_vak_afspraak'],
	['aw_fun_abb_vak', 'aw_fun_abb_vak_afspraak'],
	['aw_fks_mw.mw_nm_medewerker', 'aw_fun_nm_medewerker_afspraak'],
	['aw_fks_mw.mw_abb_medewerker', 'aw_fun_abb_medewerker_afspraak'],
	['aw_fks_lg.lg_nm_lesgroep', 'aw_fun_nm_lesgroep_afspraak'],
	['aw_fun_nm_afspraaktype', 'aw_fks_af.af_nm_afspraaktype'],
	['aw_is_presentieregistratie_verwerkt', 'aw_fks_af.af_is_presentieregistratie_verwerkt'],
	['aw_is_presentieregistratie_verplicht', 'aw_fun_is_presentieregistratie_verplicht_afspraak', 'aw_fks_af.af_is_presentieregistratie_verplicht'],
];

const equivalentFilterNames: FilterName[][] = [
	[
		'aw_nm_schooljaar',
		'bv_nm_schooljaar',
		'cf_nm_schooljaar',
		'ds_nm_schooljaar_van',
		'ekc_nm_schooljaar',
		'lb_nm_schooljaar',
		'lr_d_datum.per_nm_schooljaar',
		'pv_nm_schooljaar',
		'x_prestatieanalyse_ds_schooljaar',
		'vkk_nm_schooljaar',
	],
	['x_onderwijsresultaten_cf_schooljaar', 'x_onderwijsresultaten_ds_schooljaar', 'x_onderwijsresultaten_or_schooljaar'],
	['aw_fk_ilt.ilt_nm_niveau', 'cf_fk_ilt.ilt_nm_niveau', 'ds_fun_nm_niveau_bbs_van', 'ekc_nm_niveau'],
	['aw_nr_leerjaar', 'cf_nr_leerjaar', 'ds_nr_leerjaar_van', 'lr_nr_leerjaar', 'vkk_nr_leerjaar'],
	[
		'x_aw_schooljaar_historie',
		'x_cijfer_schooljaar_historie',
		'x_doorstroom_schooljaar_historie',
		'x_pv_schooljaar_historie',
		'x_ekc_schooljaar_historie',
		'x_vkk_schooljaar_historie',
	],
	[
		'x_aw_multiselect_schooljaar',
		'x_cijfer_multiselect_schooljaar',
		'x_doorstroom_multiselect_schooljaar',
		'x_pv_multiselect_schooljaar',
		'x_ekc_multiselect_schooljaar',
		'x_vkk_multiselect_schooljaar',
	],
	['x_details_periode', 'x_gem_periode', 'x_cijferlijst_gem_periode'],
	['x_cijferlijst_cijfertype', 'x_cijfertype'],
	['aw_fk_vs.vs_nm_vestiging', 'cf_nm_vestiging'],
	['ds_nm_cohortstatus_vanaf', 'ds_nm_cohortstatus_examenopstroom'],
	['aw_fk_lb.lb_nm_leerfase', 'bv_nm_leerfase'],
	['aw_nm_klas', 'bv_fk_lb.lb_nm_klas', 'cf_nm_klas', 'ds_nm_klas_van', 'lr_nm_klas'],
	['aw_fk_br_vest.br_co_brin', 'bv_fk_lb.lb_co_brin_vestiging', 'or_co_brinvest'],
	['aw_fk_lb.lb_nm_opleiding', 'cf_nm_opleiding', 'ds_nm_opleiding_van'],
	['aw_fk_lb.lb_is_bevorderd', 'ds_is_bevorderd'],
	['aw_fk_lb.lb_is_succesvol', 'ds_is_succesvol'],
	// Onderstaande ds_nm_uitstroom-velden bevatten (i.t.t. loopbaan_uitstroom) ook prognose-gegevens uit adviesplaatsingen, daarom zijn ze apart toegevoegd
	['aw_fk_lb.lb_nm_uitstroom_in_schooljaar', 'ds_nm_uitstroom_in_schooljaar'],
	['aw_fk_lb.lb_nm_uitstroommoment_in_schooljaar', 'ds_nm_uitstroommoment_in_schooljaar'],
	['aw_fk_lb.lb_nm_uitstroomtype_in_schooljaar', 'ds_nm_uitstroomtype_in_schooljaar'],
	...['jan', 'feb', 'mrt', 'april', 'mei', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'].map((postfix) =>
		['aw', 'bv', 'cf', 'ds', 'lr', 'vkk'].map((prefix) => <FilterName>`${prefix}_fun_is_plaatsing_peildatum_1${postfix}`)
	),
	['aw_fk_lb.lb_nm_idu', 'ds_nm_idu'],
	['aw_fk_lb_volgend_sj.lb_nm_opleiding', 'ds_nm_opleiding_naar'],
	['aw_fk_lb.lb_nm_examenstatus', 'cf_fun_nm_examenstatus_met_prognose', 'ds_fun_nm_examenstatus_met_prognose'],
	...AW_AFSPRAAK_EQUIVALENTEN,
];

const equivalentForeignKeys: Attr[][] = [
	['aw_fk_br_vest', 'cf_fk_br_vest', 'ds_fk_br_vest_van', 'ekc_fk_br_vest', 'lr_fk_br_vest'],
	['aw_fk_ilt', 'bv_fk_ilt', 'cf_fk_ilt', 'ds_fk_ilt_van', 'lr_fk_ilt', 'vkk_fk_ilt'],
	['aw_fk_ilt_volgend_sj', 'bv_fk_ilt_volgend_sj', 'cf_fk_ilt_volgend_sj', 'ds_fk_ilt_naar', 'lr_fk_ilt_volgend_sj', 'vkk_fk_ilt_volgend_sj'],
	['aw_fk_ilt_vorig_sj', 'bv_fk_ilt_vorig_sj', 'cf_fk_ilt_vorig_sj', 'ds_fk_ilt_vorig_sj', 'lr_fk_ilt_vorig_sj', 'vkk_fk_ilt_vorig_sj'],
	['aw_fk_lb', 'bv_fk_lb', 'cf_fk_lb', 'ds_fk_lb_van', 'lr_fk_lb', 'vkk_fk_lb'],
	['aw_fk_lb_volgend_sj', 'bv_fk_lb_volgend_sj', 'cf_fk_lb_volgend_sj', 'ds_fk_lb_naar', 'lr_fk_lb_volgend_sj', 'vkk_fk_lb_volgend_sj'],
	['aw_fk_lb_vorig_sj', 'bv_fk_lb_vorig_sj', 'cf_fk_lb_vorig_sj', 'ds_fk_lb_vorig_sj', 'lr_fk_lb_vorig_sj', 'vkk_fk_lb_vorig_sj'],
	['aw_fks_lg', 'cf_fk_lg', 'lr_fk_lg', 'vkk_fk_lg'],
	['aw_fk_ll', 'bv_fk_ll', 'cf_fk_ll', 'ds_fk_ll', 'lr_fk_ll', 'vkk_fk_ll'],
	['cf_fk_mw_mentor1', 'ds_fk_mw_mentor1'],
	['cf_fk_mw_mentor2', 'ds_fk_mw_mentor2'],
	['cf_fk_mw_mentor_indiv', 'ds_fk_mw_mentor_indiv'],
	['aw_fks_vk', 'cf_fk_vk', 'ekc_fk_vk', 'vkk_fk_vk'],
	['aw_fk_vs', 'bv_fk_vs', 'cf_fk_vs', 'ds_fk_vs_van', 'lr_fk_vs', 'vkk_fk_vs'],
	['aw_fk_vs_bekostiging', 'cf_fk_vs_bekostiging', 'ds_fk_vs_bekostiging_van', 'lr_fk_vs_bekostiging'],
	['aw_fks_mw', 'cf_fks_mw', 'lr_fks_mw'],
];

@Injectable({
	providedIn: 'root',
})
export class EquivalentAttService {
	equivalentFiltersList: FilterName[][];

	constructor() {
		this.equivalentFiltersList = [...equivalentFilterNames];

		for (const equivalentForeignKey of equivalentForeignKeys) {
			const foreignKey = equivalentForeignKey[0];
			const fieldsByForeignKey = getFieldsByForeignKey(foreignKey);
			if (!fieldsByForeignKey) {
				console.warn(`Foreign key ${foreignKey} not found in metadata.`);
				continue;
			}

			for (const field of fieldsByForeignKey) {
				const equivalentFields = equivalentForeignKey.map((value) => `${value}.${field}` as FilterName);

				let foundExisting = false;
				for (const equivalentFilters of this.equivalentFiltersList) {
					foundExisting = equivalentFields.some((field) => equivalentFilters.includes(field));

					if (foundExisting) {
						equivalentFilters.push(...equivalentFields);
					}
				}

				if (!foundExisting) this.equivalentFiltersList.push(equivalentFields);
			}
		}
	}

	getEquivalents(name: FilterName): FilterName[] {
		return this.equivalentFiltersList.find((filters) => filters.includes(name)) ?? [name];
	}

	/**
	 * Zet de groeperingen om die een dashboard via attOverrides heeft uitgezet naar het equivalent dat
	 * het dashboard heeft aangezet.
	 */
	naarBeschikbareGroeperingen(groeperingen: AttrPath[], overrides: AttOverrides): AttrPath[] {
		return groeperingen.map((groepering) => this.naarBeschikbareVariant(groepering, overrides));
	}

	private naarBeschikbareVariant(groepering: AttrPath, overrides: AttOverrides): AttrPath {
		const att = groepering.join('.');
		if (findOverride(overrides, att)?.grouping !== false) return groepering;

		const variant = this.getEquivalents(<FilterName>att).find((equivalent) => findOverride(overrides, equivalent)?.grouping === true);

		return variant ? <AttrPath>variant.split('.') : groepering;
	}
}
